import { create } from "zustand";

import { seedRectangle, seedWall, type Draft, type StartShape } from "@/lib/draft";

/** What the board starts from when nobody came through the Eingang. */
const FALLBACK_SEED = { widthMm: 10_000, depthMm: 8000 };

/**
 * What a new drawing by hand starts from (Task 17): **one wall**. The most
 * frequent request is a terrace along one house wall, and a wrong start is
 * cheaper this way round — one wall where a ring was meant is in plain view
 * and grows at its ends; a ring where one wall was meant leaves three walls
 * nobody ordered, to be measured by somebody who did not notice or to hold
 * the release.
 */
export const DEFAULT_START_SHAPE: StartShape = "wall";

/** A start of the given form, at the fallback's size. */
export function startDraft(shape: StartShape): Draft {
  return shape === "wall"
    ? seedWall(FALLBACK_SEED.widthMm)
    : seedRectangle(FALLBACK_SEED.widthMm, FALLBACK_SEED.depthMm);
}

/**
 * A deterministic fingerprint of what a draft would send — wall count,
 * closure, every wall's typed length and label in walk order, **every
 * vertex, and every chamfer** (wall index and both legs, in wall order, so
 * the order they were set in does not matter): two drafts with the same
 * fingerprint would send `manualOutline` the same request. A draft with no
 * chamfers fingerprints exactly as it did before chamfers existed.
 *
 * The only fingerprint left in this app. `store/planning.ts` carried a twin
 * (`outlineFingerprint`) for as long as the board kept a draft in browser
 * storage and had to decide whether a restored one still fitted; task 15
 * removed the storage and the fingerprint with it, and every gesture is
 * written to the server instead.
 *
 * The vertices are in on purpose. `pushWall` moves a wall's corners while
 * keeping its own length and label, and only unmeasures a *neighbour* — a
 * neighbour that was unmeasured already leaves count, closure, lengths and
 * labels byte-for-byte the same while the geometry on the wire moved.
 * `OutlineDraftScreen.handOver()` uses this to decide whether a cached
 * `manualOutline` answer from a failed retry still describes the draft in
 * front of the person (Fix round 3, Important 2).
 */
export function draftFingerprint(draft: Draft): string {
  return [
    draft.walls.length,
    draft.closed ? "closed" : "open",
    ...draft.walls.map((wall) => `${wall.lengthMm ?? "?"}:${wall.label ?? ""}`),
    ...draft.vertices.map((vertex) => `${vertex.xMm},${vertex.yMm}`),
    ...[...(draft.chamfers ?? [])]
      .sort((a, b) => a.wallIndex - b.wallIndex)
      .map((chamfer) => `fase${chamfer.wallIndex}:${chamfer.legAMm}x${chamfer.legBMm}`),
  ].join("|");
}

/**
 * The outline somebody is drawing, and every step it took to get there.
 *
 * A store rather than the screen's own state, for one reason that showed up
 * the first time the chain was walked end to end: pressing „Übernehmen" leaves
 * the drawing screen, and there was then **no way back** — the draft died with
 * the component, so a shape that turned out wrong on the Schema screen had to
 * be drawn again from the rectangle up. Now the two screens share it and
 * „weiter bearbeiten" is a link.
 *
 * The store itself lives in the tab. What survives a reload is a copy of the
 * shape as it stands, which the drawing screen writes to the browser per way
 * in and offers back as „Entwurf vom … wiederherstellen“
 * (`lib/outlineDraftStorage.ts`, Welle 1 idea 12) — not this history: the
 * restored drawing comes back as one step on top of the way in's first.
 */
interface OutlineDraftState {
  /** Every committed step, oldest first. `[]` before anything was started. */
  history: Draft[];
  /**
   * The steps Rückgängig took off {@link history}, the most recent last —
   * what Wiederholen puts back. Any new step empties it, and so does every
   * fresh start (`begin`, `beginWith`, „Von vorn", `clear`): none of them is
   * a gesture somebody could want back on top of what they now see.
   */
  future: Draft[];
  /** The wall the side panel is working on, or `null`. */
  selected: number | null;
  /** The corner the side panel is working on, or `null` — one of the two at a time. */
  selectedCorner: number | null;

  /** The shape as it now stands, or `null` before `begin`. */
  current(): Draft | null;
  /**
   * Start over from a rectangle of the given proportions, throwing away
   * whatever was drawn before. What „Umriss zeichnen" on the start page does.
   */
  begin(seed?: { widthMm: number; depthMm: number }): void;
  /**
   * Start over from one of the two starts („Eine Wand“ · „Rechteck“, Task
   * 17), throwing away whatever was drawn before — what „Umriss zeichnen“
   * on the start page and a new drawing for a Vorgang do with
   * {@link DEFAULT_START_SHAPE}, and what the drawing screen's choice of
   * form does. Its first step is what „Von vorn" returns to.
   */
  beginShape(shape: StartShape): void;
  /** Start from {@link DEFAULT_START_SHAPE} only if nothing is under way — reopening keeps the work. */
  ensure(): void;
  /**
   * Start over from a given shape, throwing away whatever was drawn before —
   * the outline a reading proposed (plan 4 §5, `/umriss?aus=lesung`). Its
   * first step, like `begin`'s rectangle, is what „Von vorn" returns to.
   */
  beginWith(draft: Draft): void;
  /**
   * Record a step. `live` folds a drag's hundred frames into one entry, so
   * Rückgängig undoes the gesture rather than a pixel of it.
   */
  push(next: Draft, live?: boolean): void;
  undo(): void;
  /** Whether Wiederholen has something to put back — see {@link future}. */
  canRedo(): boolean;
  /** Put back the step the last Rückgängig took. */
  redo(): void;
  /** Back to the first step this draft began with. */
  restart(): void;
  select(index: number | null): void;
  /** Pick a corner instead of a wall (Kauf-Test 11.10.2026: „Ecke entfernen“). */
  selectCorner(index: number | null): void;
  /** Nothing under way at all — used when a different drawing takes over. */
  clear(): void;
}

export const useOutlineDraft = create<OutlineDraftState>((set, get) => ({
  history: [],
  future: [],
  selected: null,
  selectedCorner: null,

  current() {
    return get().history.at(-1) ?? null;
  },

  begin(seed) {
    const { widthMm, depthMm } = seed ?? FALLBACK_SEED;
    set({ history: [seedRectangle(widthMm, depthMm)], future: [], selected: null, selectedCorner: null });
  },

  beginShape(shape) {
    get().beginWith(startDraft(shape));
  },

  ensure() {
    if (get().history.length === 0) get().beginShape(DEFAULT_START_SHAPE);
  },

  beginWith(draft) {
    set({ history: [draft], future: [], selected: null, selectedCorner: null });
  },

  push(next, live = false) {
    set((state) => ({
      history:
        live && state.history.length > 1
          ? [...state.history.slice(0, -1), next]
          : [...state.history, next],
      future: [],
    }));
  },

  undo() {
    set((state) => ({
      history: state.history.length > 1 ? state.history.slice(0, -1) : state.history,
      // Only what was actually taken: on the first step there is nothing.
      future: state.history.length > 1 ? [...state.future, state.history.at(-1)!] : state.future,
      selected: null,
      selectedCorner: null,
    }));
  },

  canRedo() {
    return get().future.length > 0;
  },

  redo() {
    const next = get().future.at(-1);
    if (next === undefined) return;
    set((state) => ({
      history: [...state.history, next],
      future: state.future.slice(0, -1),
      selected: null,
      selectedCorner: null,
    }));
  },

  restart() {
    set((state) => ({
      history: state.history.length > 0 ? [state.history[0]!] : [],
      future: [],
      selected: null,
      selectedCorner: null,
    }));
  },

  select(index) {
    set({ selected: index, selectedCorner: null });
  },

  selectCorner(index) {
    set({ selectedCorner: index, selected: null });
  },

  clear() {
    set({ history: [], future: [], selected: null, selectedCorner: null });
  },
}));
